import angular from 'angular';
import toastr from 'angular-toastr';
import 'angular-toastr/dist/angular-toastr.css';
import sweetAlert2 from 'sweetalert2'; // runs the sweet alert code listed in main field in its package.json
import 'sweetalert2/dist/sweetalert2.css';

import keyCodes from '../../util/keycodes/keycodes';

export default angular
  .module('csm.ui.alert', [toastr, keyCodes])
  .config(alertConfig)
  .factory('Alert', alertFactory).name;

alertConfig.$inject = ['toastrConfig'];
alertFactory.$inject = ['$rootScope', '$q', 'KEYCODES', 'toastr'];

/* @ngInject */ function alertConfig(toastrConfig) {
  Object.assign(toastrConfig, {
    newestOnTop: false
  });
}

/**
  * @ngdoc    factory
  * @module   csm.ui
  * @name     csm.ui.alert
  * 
  * @description
  * Shows alert messages as modal dialogs using {@link https://github.com/limonte/sweetalert2 SweetAlert2}
  * or notifications using {@link https://github.com/Foxandxss/angular-toastr toastr}. 
  * 
  * **SweetAlert2** returns a promise that resolves on a confirm('OK') button click 
  * and rejects on a cancel button or escape key.
  * 
  * **toastr** returns a notification descriptor that can be used to control the
  * notification after it has displayed.
  * 
  * @example
    <example module="alertExample">
      <file name="index.html">
        <div ng-controller="alertController">
          <table>
            <tr><td><span>Title: </span></td><td><input type="text" ng-model="title"/></td></tr>
            <tr><td><span>Message:</span></td><td><textarea rows="2" ng-model="message"></textarea><br></td></tr>
            <tr><td><span>Modal:</span></td><td><input type="checkbox" ng-model="modal"/><br></td></tr>
          </table>
          <br>
        
          <button ng-click="success()">Success</button> {{ s }}
          <button ng-click="info()">Info</button>
          <button ng-click="error()">Error</button>
          <button ng-click="confirm()">Confirm</button>
        </div>
      </file>
      <file name="script.js">
        angular.module('alertExample', ['csm.util.LocalDateSerializer'])
        angular.module("alertExample", ["csm.ui.alert"])
          .controller("alertController", function($scope, Alert, $document)
            {
            $scope.info    = function() { Alert.info   ($scope.message || "You are here",   $scope.title, $scope.modal); };
            $scope.error   = function() { Alert.error  ($scope.message || "Tisk tisk",      $scope.title, $scope.modal); };
            $scope.success = function() { Alert.success($scope.message || "Good Job",       $scope.title, $scope.modal); };
            $scope.confirm = function() { Alert.confirm($scope.message || "Do you binary?", $scope.title).then(function() 
              { 
              Alert.success("Nice", 'Well Done'); 
              });};
            })
      </file>
    </example>
  **/
/* @ngInject */ function alertFactory($rootScope, $q, KEYCODES, toastr) {
  window.swal = sweetAlert2;
  /*****************************************************************************
   * Initialize
   *****************************************************************************/
  var mSwal = sweetAlert2; // SweetAlert2 reference.

  /*-----------------------*/
  /* Supported Alert types */
  /*-----------------------*/
  var TYPE_ERROR = 'error',
    TYPE_WARNING = 'warning',
    TYPE_INFO = 'info',
    TYPE_SUCCESS = 'success';

  /*------------------------------------------------------------------*/
  /* Get Angular reference to <body> element and Add style rule to    */
  /* better indicate which button has focus in the SweetAlert2 modal. */
  /* For warning toastr notifications, set text color to black for    */
  /* better readability and contrast. The alert icon is actually an   */
  /* image so replace that with glyph icon to change its color.       */
  /*------------------------------------------------------------------*/
  var mBody = angular.element(document.querySelector('body'));
  mBody.append(
    angular.element(
      '<style>' +
        '.swal2-modal .swal2-buttonswrapper button:focus { box-shadow: 3px 3px 7px gray !important; }' +
        '.toast-warning, .toast-warning .toast-title, .toast-warning .toast-message, .toast-warning .toast-close-button { color: #000 !important; }' +
        '#toast-container .toast.toast-warning { background-image: none !important; position: relative; opacity: 1 !important; }' +
        '#toast-container .toast.toast-warning::before { font-family: "Glyphicons Halflings"; content: "\\e107"; position: absolute; left: 15px; top: 50%; transform: translateY(-50%); font-size: 20px; color: #000; }' +
        '</style>'
    )
  );

  /*****************************************************************************
   * Return Services
   *****************************************************************************/
  return {
    /*------------*/
    /* Constants. */
    /*------------*/
    TYPE_ERROR: TYPE_ERROR,
    TYPE_WARNING: TYPE_WARNING,
    TYPE_INFO: TYPE_INFO,
    TYPE_SUCCESS: TYPE_SUCCESS,

    /**
     * @ngdoc    method
     * @name     clear
     * @methodOf csm.ui.alert.service:Alert
     * @param    {array=}  toasts  toastr alert descriptors.
     * @description
     *   Closes displayed notifications.
     */
    clear: toastr.clear,

    /**
     * @ngdoc    method
     * @name     close
     * @methodOf csm.ui.alert.service:Alert
     * @description
     *   Closes modal alert dialog.
     */
    close: mSwal.close,

    /**
     * @ngdoc    method
     * @name     confirm
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string=}  title    Dialog title, default="Are You Sure?".
     * @param    {object=}  options  Additional SweetAlert options.
     * @returns  {Promise}  Promise resolved if 'Yes' selected.
     * @description
     *   Displays a modal confirm dialog using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2}.
     */
    confirm: function (message, title, options) {
      return showAlert(
        message,
        title || 'Are You Sure?',
        TYPE_WARNING,
        true,
        angular.extend(
          {
            cancelButtonText: 'No',
            confirmButtonText: 'Yes',
            showCancelButton: true
          },
          options
        )
      );
    },

    /**
     * @ngdoc    method
     * @name     error
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string=}  title    Dialog title, default="Oops".
     * @param    {boolean=} modal    `true`=show modal dialog.
     * @param    {object=}  options  SweetAlert/toastr options.
     * @returns  {Promise|object}
     *   Promise returned from **SweetAlert2** for modal dialog or notification
     *   descriptor returned from **toastr**.
     * @description
     *   Displays an error alert as a modal dialog using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2} or a notification
     *   using {@link https://github.com/Foxandxss/angular-toastr toastr}.
     */
    error: function (message, title, modal, options) {
      return showAlert(
        message,
        title || 'Oops',
        TYPE_ERROR,
        modal,
        Object.assign({ closeButton: true, timeOut: 0 }, options || {})
      );
    },

    /**
     * @ngdoc    method
     * @name     info
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string=}  title    Dialog title, default="FYI".
     * @param    {boolean=} modal    `true`=show modal dialog.
     * @param    {object=}  options  SweetAlert/toastr options.
     * @returns  {Promise|object}
     *   Promise returned from **SweetAlert2** for modal dialog or notification
     *   descriptor returned from **toastr**.
     * @description
     *   Displays an information alert as a modal dialog using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2} or a notification
     *   using {@link https://github.com/Foxandxss/angular-toastr toastr}.
     */
    info: function (message, title, modal, options) {
      return showAlert(message, title || 'FYI', TYPE_INFO, modal, options);
    },

    /**
     * @ngdoc    method
     * @name     notify
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string}   title    Notification title.
     * @param    {string}   type     Notification type.
     * @param    {object=}  options  **toastr** options.
     * @returns  {object}   Notification descriptor.
     * @description
     *   Displays a notification message alert using
     *   {@link https://github.com/Foxandxss/angular-toastr toastr}.
     */
    notify: showNotification,

    /**
     * @ngdoc    method
     * @name     warning
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string=}  title    Dialog title, default="Warning".
     * @param    {boolean=} modal    `true`=show modal dialog.
     * @param    {object=}  options  SweetAlert/toastr options.
     * @returns  {Promise|object}
     *   Promise returned from **SweetAlert2** for modal dialog or notification
     *   descriptor returned from **toastr**.
     * @description
     *   Displays a warning alert as a modal dialog using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2} or a notification
     *   using {@link https://github.com/Foxandxss/angular-toastr toastr}.
     */
    warning: function (message, title, modal, options) {
      return showAlert(
        message,
        title || 'Warning',
        TYPE_WARNING,
        modal,
        Object.assign({ closeButton: true, timeOut: 0 }, options || {})
      );
    },

    /**
     * @ngdoc    method
     * @name     success
     * @methodOf csm.ui.alert.service:Alert
     * @param    {string}   message  Alert message.
     * @param    {string=}  title    Dialog title, default="All Good".
     * @param    {boolean=} modal    `true`=show modal dialog.
     * @param    {object=}  options  SweetAlert/toastr options.
     * @returns  {Promise|object}
     *   Promise returned from **SweetAlert2** for modal dialog or notification
     *   descriptor returned from **toastr**.
     * @description
     *   Displays a success alert as a modal dialog using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2} or a notification
     *   using {@link https://github.com/Foxandxss/angular-toastr toastr}.
     */
    success: function (message, title, modal, options) {
      return showAlert(
        message,
        title || 'All Good',
        TYPE_SUCCESS,
        modal,
        options
      );
    },

    /**
     * @ngdoc    method
     * @name     show
     * @methodOf csm.ui.alert.service:Alert
     * @param    {object}  options  **SweetAlert2** options.
     * @returns  {Promise|object}
     *   Promise returned from **SweetAlert2**.
     * @description
     *   Displays a modal dialog message alert using
     *   {@link https://github.com/limonte/sweetalert2 SweetAlert2}.
     */
    show: showDialog
  };

  /*****************************************************************************
   * escapeListener
   *
   * Parameters: event  KeyPress event.
   *
   * Keydown listener function that listens for the escape key being pressed. When
   * an escape key is pressed, the event will have the default prevented so that
   * no other listeners will respond to the escape key.
   *
   * This will be used with the SweetAlert dialogs being shown so any underlying
   * dialogs will not close.
   *****************************************************************************/
  function escapeListener(event) {
    /*-----------------------------------------*/
    /* Do nothing if the event is already      */
    /* prevented or this is not the escape key */
    /*-----------------------------------------*/
    if (event.isDefaultPrevented() || event.which != KEYCODES.ESCAPE) return;

    event.preventDefault();
  }

  /*****************************************************************************
   * showAlert
   *
   * Parameters: message  Message to be displayed with the alert.
   *             title    Message title.
   *             type     Message type.
   *             modal    Indication create a modal alert.
   *             options  Additional alert options to set.
   * Returns:             Promise from SweetAlert2 or descriptor from toastr.
   *
   * This method is used to determine which messenging service to use to process
   * the alert. When modal is true, the SweetAlert utility is used, otherwise
   * the toastr service will be used. The options provided should adhere to the
   * appropriate tool.
   *
   * NOTE: The options parameter should be generic to the tool and have the
   *       methods parse the values provided.
   *****************************************************************************/
  function showAlert(message, title, type, modal, options) {
    return modal
      ? showDialog(
          angular.extend({ title: title, text: message, type: type }, options)
        )
      : showNotification(message, title, type, options);
  }

  /*****************************************************************************
   * showDialog
   *
   * Parameters: options  SweetAlert2 options.
   * Returns:             Promise resolved/rejected on Ok/Cancel.
   *
   * Use the SweetAlert utility to display a modal alert utilizing the passed
   * options parameter which will be the SweetAlert options. This method will
   * return a promise that can be listened for the closing of the alert either
   * through a confirm or cancel action.
   *****************************************************************************/
  function showDialog(options) {
    var defer = $q.defer();

    /*-----------------------------------------------*/
    /* Set listener so only the alert will be closed */
    /*-----------------------------------------------*/
    mBody.on('keydown', escapeListener);

    /** Utility function to remove the listener and use the callback(cb) function
      in the angular scope. */
    function finish(cb) {
      mBody.off('keydown', escapeListener);
      $rootScope.$apply(cb);
    }

    /*----------------------------------------*/
    /* Utilize the doneFunction parameter to  */
    /* be notified when the dialog is removed */
    /*----------------------------------------*/
    mSwal(options).then(
      function (result) {
        finish(function () {
          defer.resolve(result);
        });
      },
      function (how) {
        finish(function () {
          defer.reject(how);
        });
      }
    );

    return defer.promise;
  }

  /*****************************************************************************
   * showNotification
   *
   * Parameters: message  Notification message.
   *             title    Message title.
   *             type     Notification type.
   *             options  toastr options.
   * Returns:             Notification descriptor.
   *
   * Use the toastr service to display a toaster style notification. This will
   * extend the options parameter with a hidden handler so the returned promise
   * can be resolved when the notification is removed.
   *****************************************************************************/
  function showNotification(message, title, type, options) {
    var defer = $q.defer();

    var opts = angular.extend({}, options, {
      onHidden: function () {
        defer.resolve();
      }
    });
    toastr[type](message, title, opts);

    return defer.promise;
  }
}

/**
 * TODO PK
 * Look into this
 * I don't believe the below polyfills will be necessary due to the fact that we
 * have now implemented babel/ browserify and corejs 3 for polyfills.
 * Should be ripped out
 */

/*******************************************************************************
 * Polyfill for the classList property of DOM elements. Required for use with
 * SweetAlert2 to function in IE.
 * see: https://developer.mozilla.org/en-US/docs/Web/API/Element/classList
 *******************************************************************************/
/*---------*/
/* helpers */
/*---------*/
var regExp = function (name) {
  return new RegExp('(^| )' + name + '( |$)');
};
var forEach = function (list, fn, scope) {
  for (var i = 0; i < list.length; i++) {
    fn.call(scope, list[i]);
  }
};

/*--------------------------------------*/
/* class list object with basic methods */
/*--------------------------------------*/
function ClassList(element) {
  this.element = element;
}

ClassList.prototype = {
  add: function () {
    forEach(
      arguments,
      function (name) {
        if (!this.contains(name)) {
          this.element.className +=
            this.element.className.length > 0 ? ' ' + name : name;
        }
      },
      this
    );
  },
  remove: function () {
    forEach(
      arguments,
      function (name) {
        this.element.className = this.element.className.replace(
          regExp(name),
          ''
        );
      },
      this
    );
  },
  toggle: function (name) {
    return this.contains(name)
      ? (this.remove(name), false)
      : (this.add(name), true);
  },
  contains: function (name) {
    return regExp(name).test(this.element.className);
  },
  // bonus..
  replace: function (oldName, newName) {
    (this.remove(oldName), this.add(newName));
  }
};

/*---------------*/
/* IE8/9, Safari */
/*---------------*/
if (!('classList' in Element.prototype)) {
  Object.defineProperty(Element.prototype, 'classList', {
    get: function () {
      return new ClassList(this);
    }
  });
}

/*------------------------------*/
/* replace() support for others */
/*------------------------------*/
if (window.DOMTokenList && DOMTokenList.prototype.replace == null) {
  DOMTokenList.prototype.replace = ClassList.prototype.replace;
}
